/**
 * studioFormAdapter.ts
 *
 * Converts a flat Studio RJSF schema (one object, all fields, sections in metadata)
 * into the multi-step array format expected by a Studio-driven form renderer.
 *
 * Studio schema shape  →  Step array shape
 * ─────────────────────────────────────────
 * { schema, uiSchema }  →  Array<StepSchema>
 *
 * Each section in `schema.x-crosure.sections` becomes one step.
 * Fields are assigned to steps via `uiSchema[field]["ui:section"]`.
 * Fields with no section go into a catch-all final step.
 */

import type {
	StudioApiClient,
	StudioFormSchemaResponse,
	StudioFieldSchema,
	StudioUiEntry,
	StudioFormContract,
	StudioWizardFormSchemaResponse,
} from './studioApi'
import { tenantBrandingFromMeta, type TenantBranding } from './tenantFormat'
import type { RJSFSchema } from '@rjsf/utils'

export interface StepSchema extends Omit<RJSFSchema, 'properties'> {
	sequence: number
	formId: string
	title: string
	description?: string
	properties: Record<string, StudioFieldSchema>
	required: string[]
	uiSchema: Record<string, unknown>
	/** Set on the synthetic payment step so it can re-price via Studio. */
	formCode?: string
	button?: {
		label?: string
		subtext?: string
		hidden?: boolean
	}
	'x-crosure-step-field'?: string
}

/** Default section-level button labels shown on the "Next" button. Override
 *  per consumer via adaptStudioSchema's `sectionButtons` option. */
export const DEFAULT_SECTION_BUTTONS: Record<
	string,
	{ label: string; subtext?: string }
> = {
	address: { label: 'Property details', subtext: 'Next' },
	covertype: { label: 'Property details', subtext: 'Next' },
	property: { label: 'Security', subtext: 'Next' },
	security: { label: 'Residents', subtext: 'Next' },
	habitants: { label: 'Claims', subtext: 'Next' },
	claims: { label: 'Building', subtext: 'Next' },
	building: { label: 'Contents', subtext: 'Next' },
	contents: { label: 'Get quote', subtext: 'Next' },
	quote: { label: 'Legal Statement', subtext: 'Next' },
	legal_statement: { label: 'Terms', subtext: 'Next' },
	terms: { label: 'Your Details', subtext: 'Next' },
	your_details: { label: 'Confirm', subtext: 'Next' },
	smsotp: { label: 'Go to payment', subtext: 'Next' },
}

/** Default UK address field codes that trigger the address-step special-case.
 *  Override per consumer via adaptStudioSchema's `addressFields` option. */
export const DEFAULT_ADDRESS_FIELDS = new Set([
	'postcode',
	'address1',
	'address2',
	'citytown',
	'building_number_name',
	'buildingNumberOrName',
])

/**
 * Widget names a renderer can actually resolve — the union of a consumer's
 * custom widgets plus RJSF/@rjsf built-in widget aliases.
 *
 * If Studio sends a `ui:widget` not in this set (e.g. a new widget type added
 * in Studio that the renderer doesn't implement yet), RJSF throws
 * "No widget 'X' for type 'Y'" and the whole form crashes. We instead drop the
 * unknown `ui:widget` so RJSF falls back to the default input for the field's
 * type — the field still renders and is usable.
 */
const DEFAULT_SUPPORTED_WIDGETS = new Set<string>([
	// custom (crosure-lite-studio's widgets.ts as of this extraction)
	'CheckboxWidget',
	'CheckboxesWidget',
	'ContentsCover',
	'DateWidget',
	'DateWidgetWithTodayButton',
	'UpDownWidget',
	'currency',
	'helpBox',
	'radioArray',
	'radioCards',
	'radioCardsRow',
	'SelectWidget',
	'StepWidget',
	'HybridRangeWidget',
	'monthYear',
	'YearWidget',
	'yearOfBirthGroup',
	'LocksWidget',
	'postcodeLookup',
	'postcodeAddressLookup',
	// RJSF / chakra built-in aliases
	'text',
	'textarea',
	'password',
	'email',
	'url',
	'uri',
	'data-url',
	'color',
	'date',
	'datetime',
	'time',
	'select',
	'radio',
	'checkbox',
	'checkboxes',
	'hidden',
	'updown',
	'range',
	'file',
])

/**
 * Named per-field template overrides (`ui:ArrayFieldTemplate`, `ui:ObjectFieldTemplate`,
 * `ui:FieldTemplate`) a renderer can actually resolve to a component. Empty by
 * default — add entries via the `supportedTemplates` option once a named
 * template is actually implemented and registered in the consumer app.
 */
const SUPPORTED_TEMPLATE_KEYS = [
	'ui:ArrayFieldTemplate',
	'ui:ObjectFieldTemplate',
	'ui:FieldTemplate',
] as const

/**
 * Drop any `ui:widget` or named template override (`ui:ArrayFieldTemplate` etc.)
 * the renderer can't resolve, so an unknown value degrades to the default
 * input/template instead of crashing the form. RJSF expects these template
 * keys to hold an actual component reference — a plain string with no
 * matching implementation resolves to `undefined` and throws a hard render
 * error, exactly like an unknown `ui:widget` used to before this guard
 * existed. Returns a new object; never mutates the input.
 */
function sanitizeUiEntry(
	uiEntry: Record<string, unknown>,
	fieldCode: string,
	supportedWidgets: Set<string>,
	supportedTemplates: Set<string>
): Record<string, unknown> {
	let result = uiEntry
	const widget = result['ui:widget']
	if (typeof widget === 'string' && !supportedWidgets.has(widget)) {
		// eslint-disable-next-line no-console
		console.warn(
			`[studioFormAdapter] Unknown ui:widget "${widget}" on field "${fieldCode}" — ` +
				`falling back to the default input. Register it to support it.`
		)
		const { 'ui:widget': _drop, ...rest } = result
		result = rest
	}
	for (const key of SUPPORTED_TEMPLATE_KEYS) {
		const value = result[key]
		if (typeof value === 'string' && !supportedTemplates.has(value)) {
			// eslint-disable-next-line no-console
			console.warn(
				`[studioFormAdapter] Unknown ${key} "${value}" on field "${fieldCode}" — ` +
					`falling back to the default template so the form doesn't crash.`
			)
			const { [key]: _drop, ...rest } = result
			result = rest
		}
	}
	return result
}

/**
 * Normalise a single Studio field schema into valid RJSF.
 *
 * Studio's multi-select fields are exported as `{ type: "string", enum: [...] }`
 * with `ui:widget: "checkboxes"`. RJSF's `checkboxes` widget only works on an
 * ARRAY type — a string + checkboxes combination throws
 * "No widget 'checkboxes' for type 'string'" and crashes the form.
 *
 * Here we detect that case (widget is checkboxes/multiselect, type is string,
 * an enum is present) and rewrite it to the array shape RJSF expects:
 *   { type: "array", uniqueItems: true, items: { type, enum, enumNames } }
 *
 * This is defensive — Studio's producer has been fixed to emit the array shape
 * directly, but a bundled fallback JSON or any pre-fix export still carry the
 * invalid shape, so we repair it at the adapter boundary.
 */
function normaliseFieldSchema(
	prop: StudioFieldSchema,
	uiEntry: StudioUiEntry | undefined
): StudioFieldSchema {
	const widget = uiEntry?.['ui:widget']
	const isMultiWidget = widget === 'checkboxes' || widget === 'multiselect'
	const looksLikeMultiSelect =
		isMultiWidget && prop?.type === 'string' && Array.isArray(prop?.enum)

	if (!looksLikeMultiSelect) return prop

	const { enum: enumValues, enumNames, type: _t, ...rest } = prop as any
	return {
		...rest,
		type: 'array',
		uniqueItems: true,
		items: {
			type: 'string',
			enum: enumValues,
			...(enumNames ? { enumNames } : {}),
		},
	} as StudioFieldSchema
}

export interface AdaptStudioSchemaOptions {
	/** Section-level button labels. Defaults to DEFAULT_SECTION_BUTTONS. */
	sectionButtons?: Record<string, { label: string; subtext?: string }>
	/** Field codes that trigger the address-step special-case. Defaults to
	 *  DEFAULT_ADDRESS_FIELDS. */
	addressFields?: Set<string>
	/** Widget names the consumer's renderer can resolve. Defaults to
	 *  DEFAULT_SUPPORTED_WIDGETS. */
	supportedWidgets?: Set<string>
	/** Named template overrides the consumer's renderer can resolve. Defaults
	 *  to an empty set (no named templates registered). */
	supportedTemplates?: Set<string>
}

/**
 * Adapt a Studio flat RJSF schema into an ordered array of step schemas.
 *
 * Handles two response shapes:
 *   - Live API  → sections in `meta.sections[].section_code` + `fields[]`
 *   - Static export → sections in `schema['x-crosure'].sections[].code`,
 *                     fields derived via `uiSchema[field]['ui:section']`
 */
export function adaptStudioSchema(
	studioSchema: StudioFormSchemaResponse,
	options: AdaptStudioSchemaOptions = {}
): StepSchema[] {
	const sectionButtons = options.sectionButtons ?? DEFAULT_SECTION_BUTTONS
	const addressFields = options.addressFields ?? DEFAULT_ADDRESS_FIELDS
	const supportedWidgets = options.supportedWidgets ?? DEFAULT_SUPPORTED_WIDGETS
	const supportedTemplates = options.supportedTemplates ?? new Set<string>()

	const { schema, uiSchema, meta } = studioSchema

	// Resolve sections from whichever source is present
	const rawSections = meta?.sections ?? schema['x-crosure']?.sections ?? []

	const sections = [...rawSections].sort(
		(a, b) => a.display_order - b.display_order
	)

	const globalRequired = new Set(schema.required)

	// Build fieldsBySection — live API provides fields[] directly on each section;
	// static export tags each field with ui:section in uiSchema.
	const fieldsBySection: Record<string, string[]> = {}

	for (const sec of sections) {
		const code = sec.section_code ?? sec.code ?? ''
		if (!code) continue
		if (Array.isArray(sec.fields) && sec.fields.length > 0) {
			// Live API path — fields list is explicit
			fieldsBySection[code] = sec.fields
		}
	}

	// Static export path — derive from uiSchema ui:section tags
	if (Object.keys(fieldsBySection).length === 0) {
		for (const [fieldCode, uiEntry] of Object.entries(uiSchema)) {
			if (fieldCode === 'ui:order') continue
			const entry = uiEntry as StudioUiEntry
			const sectionCode = entry['ui:section']
			if (sectionCode) {
				if (!fieldsBySection[sectionCode]) fieldsBySection[sectionCode] = []
				fieldsBySection[sectionCode].push(fieldCode)
			}
		}
		// Sort static-derived fields by ui:order
		for (const code of Object.keys(fieldsBySection)) {
			const list = fieldsBySection[code]
			if (!list) continue
			list.sort((a, b) => {
				const aOrder = (uiSchema[a] as StudioUiEntry)?.['ui:order'] ?? 999
				const bOrder = (uiSchema[b] as StudioUiEntry)?.['ui:order'] ?? 999
				return aOrder - bOrder
			})
		}
	}

	// Catch-all: any known field NOT assigned to a section would otherwise be
	// silently dropped — never rendered, never submitted — and if it's required,
	// Studio rejects the submission with a confusing error. Collect such fields
	// into a final "Additional details" step so nothing is lost.
	const assignedCodes = new Set(Object.values(fieldsBySection).flat())
	const unsectioned = Object.keys(schema.properties ?? {}).filter(
		code => !assignedCodes.has(code)
	)
	if (unsectioned.length > 0) {
		// eslint-disable-next-line no-console
		console.warn(
			`[studioFormAdapter] ${unsectioned.length} field(s) not assigned to any ` +
				`section: ${unsectioned.join(', ')} — appended to a catch-all step ` +
				`so they still render and submit.`
		)
		const catchAllCode = '_additional'
		fieldsBySection[catchAllCode] = unsectioned
		sections.push({
			section_code: catchAllCode,
			code: catchAllCode,
			title: 'Additional details',
			display_order: 9999,
		} as (typeof sections)[number])
	}

	const steps: StepSchema[] = sections
		.map(sec => ({ ...sec, _code: sec.section_code ?? sec.code ?? '' }))
		.filter(sec => (fieldsBySection[sec._code]?.length ?? 0) > 0)
		.map((sec, idx) => {
			const fieldCodes = fieldsBySection[sec._code] ?? []
			const stepProperties: Record<string, StudioFieldSchema> = {}
			const stepRequired: string[] = []
			const stepUiSchema: Record<string, unknown> = {
				'ui:order': fieldCodes,
			}

			for (const code of fieldCodes) {
				if (schema.properties[code]) {
					stepProperties[code] = normaliseFieldSchema(
						schema.properties[code],
						uiSchema[code] as StudioUiEntry
					)
				}
				if (globalRequired.has(code)) {
					stepRequired.push(code)
				}
				// Strip the ui:section key — not needed per-step
				const { 'ui:section': _sec, ...uiRest } =
					(uiSchema[code] as StudioUiEntry) ?? {}
				// Drop any widget the renderer can't resolve so the form never
				// crashes on a widget type Studio added that the consumer doesn't have.
				const safeUi = sanitizeUiEntry(
					uiRest,
					code,
					supportedWidgets,
					supportedTemplates
				)
				if (Object.keys(safeUi).length > 0) {
					stepUiSchema[code] = safeUi
				}
			}

			// Every step MUST have a button or the page renders no forward/submit
			// control and the user gets stuck. Sections Studio adds that aren't in
			// the curated label map fall back to a generic "Continue".
			const btn = sectionButtons[sec._code] ?? {
				label: 'Continue',
				subtext: 'Next',
			}

			// ── Address step special-case ────────────────────────────────────
			// When a section contains the standard UK address fields, delegate
			// rendering to the consumer's AddressField component (`ui:field: address`).
			// That component owns the full address UX: postcode input, "Find" button
			// calling an address-lookup API, dropdown of results, and manual fallback
			// inputs. Rendering the fields individually (the Studio default) gives a
			// plain text input per field with no lookup capability.
			//
			// The AddressField reads field titles/labels from schema.properties, so
			// we augment them with the extra metadata it expects (button label, error
			// text, select placeholder).
			//
			// Studio can now author the lookup itself, as a `postcodeAddressLookup`
			// widget on the postcode binding carrying the integration, the copy and
			// the populate map. That widget writes into the address fields as
			// ordinary siblings, so handing the whole step to AddressField instead
			// would hide the very fields it targets — and replace Studio-authored
			// copy with AddressField's hardcoded English. Where Studio has said how
			// the address step should render, it wins.
			const hasAuthoredLookup = fieldCodes.some(
				c =>
					(uiSchema[c] as StudioUiEntry | undefined)?.['ui:widget'] ===
					'postcodeAddressLookup'
			)
			const isAddressStep =
				!hasAuthoredLookup &&
				(sec._code === 'address' ||
					fieldCodes.filter(c => addressFields.has(c)).length >= 3)

			if (isAddressStep) {
				// Merge address-field metadata the AddressField component needs
				const addressProps = { ...stepProperties }
				if (addressProps.postcode) {
					addressProps.postcode = {
						...addressProps.postcode,
						button: 'Find my address',
						error:
							'No matching addresses found. Check and try again, or enter the information below.',
					}
				}
				if (addressProps.address1) {
					addressProps.address1 = {
						...addressProps.address1,
						select: 'Select address',
					}
				}
				// IMPORTANT: keep Studio's field codes verbatim (e.g.
				// `building_number_name`). Do NOT rename to camelCase — the value
				// the user enters must be submitted under the code Studio validates
				// against. AddressField reads/writes whichever building-field code
				// is present (building_number_name or legacy buildingNumberOrName).

				return {
					sequence: idx,
					formId: sec._code,
					title: sec.title,
					description: '',
					type: 'object',
					properties: addressProps,
					required: stepRequired,
					uiSchema: { 'ui:field': 'address' },
					button: { label: btn.label, subtext: btn.subtext },
				}
			}

			return {
				sequence: idx,
				formId: sec._code,
				title: sec.title,
				description: '',
				type: 'object',
				properties: stepProperties,
				required: stepRequired,
				uiSchema: stepUiSchema,
				button: { label: btn.label, subtext: btn.subtext },
			}
		})

	return ensurePaymentStep(
		steps,
		meta?.product_code ?? schema['x-crosure']?.product_code ?? null,
		meta?.form_code ?? schema['x-crosure']?.form_code
	)
}

// ── Payment step ───────────────────────────────────────────────────
// The payment step is a renderer concern (ui:field: payment), not a form
// field — bundled schemas hand-author it, but a live Studio form has none.
// Append one when the form has a product to charge for and a payment step
// isn't already present, so Studio-driven journeys can take payment.
//
// Shared by both adaptStudioSchema (flat/legacy schema shape) AND
// loadStudioSteps' wizard-v1 branch (Studio's own pre-built steps) — Studio's
// backend step builder (build_rjsf_wizard_for_form) only ever slices the
// form's real sections, it never synthesizes a payment step itself, so
// wizard-v1 responses need this exact same injection or the payment step
// silently never exists for any form using that contract.
export function ensurePaymentStep(
	steps: StepSchema[],
	productCode: string | null | undefined,
	formCode: string | undefined
): StepSchema[] {
	const existingPaymentStepIndex = steps.findIndex(
		s =>
			s.formId === 'payment' ||
			(s.uiSchema as Record<string, unknown>)?.['ui:field'] === 'payment'
	)
	// A step can be payment-shaped (formId: 'payment') WITHOUT already carrying
	// `ui:field: payment` — Studio's own schema builder has no way to express
	// that RJSF override, per the comment above. Previously, finding such a
	// step just skipped the injection below entirely, leaving that step with
	// no `ui:field` override at all — RJSF then rendered it as a plain object,
	// so the whole Payment UI (price, button, Stripe Elements) silently never
	// rendered. Merge the override into the existing step instead of assuming
	// its presence.
	const existingStep =
		existingPaymentStepIndex !== -1 ? steps[existingPaymentStepIndex] : undefined
	// A form may point its payment step at its OWN component rather than the
	// generic one — inson's Studio schema declares `ui:field: insonPayment`,
	// because payment sits mid-journey there and the generic field assumes it
	// ends the journey. Forcing 'payment' below would silently render the wrong
	// component with no error, so keep whatever the form declared and only
	// default when it declared nothing.
	const declaredField = (existingStep?.uiSchema as Record<string, unknown>)?.[
		'ui:field'
	] as string | undefined
	if (existingStep && declaredField !== 'payment') {
		// Also clear properties/required to match the synthetic step below
		// exactly, not just add ui:field alongside whatever Studio's schema
		// happened to declare. The Payment component renders its own UI
		// entirely and never reads RJSF-tracked form data for this step, so
		// keeping a non-empty properties object serves no rendering purpose —
		// but it does change which code path RJSF's schema resolution takes
		// (retrieveSchema does substantially more work for a non-empty object
		// schema than an empty one), which can silently drop the custom
		// top-level keys (prevInputs, quoteId) this component depends on.
		// Matching the known-working empty-properties shape sidesteps that
		// entirely rather than depending on RJSF's exact key-preservation
		// behavior for a non-trivial schema.
		steps[existingPaymentStepIndex] = {
			...existingStep,
			properties: {},
			required: [],
			uiSchema: { 'ui:field': declaredField ?? 'payment' },
			// The real per-section step-building loop above never attaches
			// formCode (only the synthetic step below did) — <Payment> reads
			// props.schema.formCode directly and needs it for every
			// Studio-mode call (payment-methods, payment-intent,
			// studio-quote-preview re-pricing). Without it those all 400 with
			// "formCode query param is required", which is silent from the
			// customer's POV since Payment.tsx's own re-price effect swallows
			// a non-ok response and just leaves the price blank.
			formCode,
		}
	}
	const hasPaymentStep = existingPaymentStepIndex !== -1
	if (productCode && !hasPaymentStep) {
		// Root-level `ui:field: payment` with empty properties — RJSF then renders
		// the Payment component for the whole step, so the page-injected
		// prevInputs/quoteId land on the field's schema (a property-level ui:field
		// would only get the bare property schema → "no previous inputs").
		steps.push({
			sequence: steps.length,
			formId: 'payment',
			title: 'Payment',
			description: '',
			type: 'object',
			properties: {},
			required: [],
			uiSchema: { 'ui:field': 'payment' },
			button: { label: 'Pay now', subtext: 'Secure payment' },
			// So the payment step can re-price via Studio if the premium wasn't
			// computed earlier (a journey without a 'quote' step).
			formCode,
		} as unknown as StepSchema)
	}

	return steps
}

export interface LoadStudioStepsResult {
	/** Multi-step form schemas adapted for the consumer's renderer */
	steps: StepSchema[]
	/** Studio form_code — the client must include this when submitting */
	formCode: string
	/** Where the schema came from — 'live' (Studio API) or 'fallback' (bundled JSON).
	 *  Surfaced so a demo can VERIFY it's showing live edits, not the stale fallback. */
	source: 'live' | 'fallback'
	/** Reason the live fetch failed (only set when source === 'fallback'). */
	fallbackReason?: string
	/** True when Studio authoritatively rejected the form as not published
	 *  (HTTP 403). The caller should 404 rather than render the bundled copy. */
	notPublished?: boolean
	/** Tenant branding (logo/primary color/tagline) from the schema meta, so the
	 *  consumer app themes per tenant at runtime — no per-client theme file. */
	branding?: TenantBranding
}

/**
 * Resolve the canonical form_code from either the live API response (meta.form_code)
 * or the static export (schema['x-crosure'].form_code). Falls back to the requested
 * code so we never lose track of what to submit to.
 */
function resolveFormCode(
	studioSchema: StudioFormSchemaResponse,
	requestedCode: string
): string {
	return (
		studioSchema.meta?.form_code ??
		studioSchema.schema['x-crosure']?.form_code ??
		requestedCode
	)
}

export interface LoadStudioStepsOptions extends AdaptStudioSchemaOptions {
	locale?: string
	/** Request Studio's pre-adapted wizard contract instead of the flat
	 *  schema (see StudioFormContract). Omit to keep the legacy flat-schema +
	 *  adaptStudioSchema flow every existing caller already relies on — if
	 *  Studio doesn't return the wizard shape even when this IS set (older
	 *  deployment, form not opted in), loadStudioSteps still falls back to
	 *  adaptStudioSchema automatically, so setting this is always safe. */
	contract?: StudioFormContract
	/** Load a bundled/static fallback schema for a form code when the live
	 *  Studio fetch fails (timeout/5xx/network — NOT a 403, which is
	 *  authoritative "not published" and must not fall back). Each consumer
	 *  app owns its own bundled JSON (path, bundling mechanism), so this is
	 *  injected rather than assumed. Omit to disable the fallback (steps come
	 *  back empty with `source: 'fallback'` and a fallbackReason). */
	loadFallbackSchema?: (formCode: string) => Promise<StudioFormSchemaResponse>
}

/**
 * True when Studio returned the pre-adapted wizard-v1 contract shape
 * instead of the flat legacy schema — i.e. `steps` are already built and
 * must NOT be passed through adaptStudioSchema. This checks the response
 * itself (not just whether a contract was requested) so a caller that asks
 * for wizard-v1 against a Studio deployment/form that doesn't support it yet
 * still degrades safely to the flat-schema flow.
 */
export function isWizardFormSchemaResponse(
	response: StudioFormSchemaResponse | StudioWizardFormSchemaResponse
): response is StudioWizardFormSchemaResponse {
	return (
		(response as StudioWizardFormSchemaResponse)?.contract_version === 'wizard-v1'
	)
}

/**
 * Load a Studio form schema via the given client, with an optional fallback
 * to a consumer-supplied bundled static schema on connectivity failure.
 */
export async function loadStudioSteps(
	client: StudioApiClient,
	formCode: string,
	options: LoadStudioStepsOptions = {}
): Promise<LoadStudioStepsResult> {
	const locale = options.locale ?? 'en'
	let fallbackReason: string | undefined

	try {
		const studioSchema = await client.fetchStudioFormSchema(
			formCode,
			locale,
			options.contract
		)
		if (isWizardFormSchemaResponse(studioSchema)) {
			const wizardFormCode = studioSchema.meta?.form_code ?? formCode
			return {
				steps: ensurePaymentStep(
					studioSchema.steps as StepSchema[],
					studioSchema.meta?.product_code ?? null,
					wizardFormCode
				),
				formCode: wizardFormCode,
				source: 'live',
				branding: tenantBrandingFromMeta(studioSchema.meta),
			}
		}
		return {
			steps: adaptStudioSchema(studioSchema, options),
			formCode: resolveFormCode(studioSchema, formCode),
			source: 'live',
			branding: tenantBrandingFromMeta(studioSchema.meta),
		}
	} catch (err) {
		fallbackReason = err instanceof Error ? err.message : String(err)
		// A 403 is authoritative: the form is intentionally not published
		// (draft/deprecated). Do NOT serve the stale bundled copy — signal the
		// caller to 404. The bundled fallback is only for connectivity failures
		// (timeout / 5xx / network), where resilience is the right call.
		const httpStatus = (err as { status?: number })?.status
		if (httpStatus === 403) {
			return {
				steps: [],
				formCode,
				source: 'live',
				fallbackReason,
				notPublished: true,
			}
		}
		console.warn(
			`[studioFormAdapter] Live API fetch failed, falling back to static JSON: ${fallbackReason}`
		)
	}

	// Fallback: bundled static export from Studio. Only some form codes have a
	// bundled JSON. For tenant-specific forms with no bundle, a missing
	// fallback must NOT throw — return empty steps so the caller can fall back
	// to its own CMS schema / handle it.
	if (!options.loadFallbackSchema) {
		return {
			steps: [],
			formCode,
			source: 'fallback',
			fallbackReason,
		}
	}
	try {
		const staticSchema = await options.loadFallbackSchema(formCode)
		return {
			steps: adaptStudioSchema(staticSchema, options),
			formCode: resolveFormCode(staticSchema, formCode),
			source: 'fallback',
			fallbackReason,
			branding: tenantBrandingFromMeta(staticSchema.meta),
		}
	} catch {
		return {
			steps: [],
			formCode,
			source: 'fallback',
			fallbackReason: `${fallbackReason}; no bundled schema for '${formCode}'`,
		}
	}
}
